import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  Animated,
  ActivityIndicator,
  Modal,
  Pressable,
  useWindowDimensions,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { Image as ExpoImage } from 'expo-image';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import { useTheme } from '../context/ThemeContext';
import { getApiUrl, formatApiFetchError } from '../utils/apiConfig';
import {
  setLoggedInStudentId,
  setUserSnapshot,
  normalizeStudentUserRecord,
} from '../utils/sessionUser';
import { useUserPreferences } from '../context/UserPreferencesContext';
import { invalidateStudentProfilePictureCache } from '../utils/studentProfilePhoto';
import TwoFactorResetModal from '../components/TwoFactorResetModal';

const LOGO_URL = 'https://mingchuan.ie/wp-content/uploads/2016/12/cropped-head.jpg';
const LOGIN_GOLD = '#d4af37';
const QUOTE_ROTATE_MS = 10000;
const MOBILE_WEB_MAX_WIDTH = 768;

function quoteTypography(text, compact) {
  const len = String(text || '').length;
  if (compact) {
    if (len > 160) return { fontSize: 10, lineHeight: 14 };
    if (len > 110) return { fontSize: 11, lineHeight: 15 };
    if (len > 70) return { fontSize: 12, lineHeight: 16 };
    return { fontSize: 13, lineHeight: 17 };
  }
  if (len > 180) return { fontSize: 11, lineHeight: 16 };
  if (len > 120) return { fontSize: 12, lineHeight: 17 };
  if (len > 80) return { fontSize: 13, lineHeight: 18 };
  return { fontSize: 14, lineHeight: 20 };
}

async function fetchRandomQuoteFromApi() {
  try {
    const response = await fetch(`${getApiUrl()}/api/quotes/random`);
    if (!response.ok) return null;
    const data = await response.json();
    if (!data?.quote) return null;
    return data;
  } catch {
    return null;
  }
}

async function parseJsonResponse(response) {
  try {
    return await response.json();
  } catch {
    return {};
  }
}

export default function LoginScreen({ navigation }) {
  const theme = useTheme();
  const { setIsUSUserFromUser } = useUserPreferences();
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const isMobileWeb = Platform.OS === 'web' && windowWidth < MOBILE_WEB_MAX_WIDTH;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [loginPending, setLoginPending] = useState(false);

  const [loginStep, setLoginStep] = useState('credentials');
  const [loginPendingToken, setLoginPendingToken] = useState(null);
  const [pendingUser, setPendingUser] = useState(null);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState(null);
  const [otpCode, setOtpCode] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [showBackupCodesModal, setShowBackupCodesModal] = useState(false);
  const [setupBackupCodes, setSetupBackupCodes] = useState([]);
  const [pendingLoginAfterSetup, setPendingLoginAfterSetup] = useState(null);
  const [showReset2faModal, setShowReset2faModal] = useState(false);
  const [reset2faPassword, setReset2faPassword] = useState('');
  const [quote, setQuote] = useState(null);

  const buttonScale = useRef(new Animated.Value(1)).current;

  const loadRandomQuote = useCallback(async () => {
    const next = await fetchRandomQuoteFromApi();
    if (next) setQuote(next);
  }, []);

  useEffect(() => {
    void loadRandomQuote();
    const timer = setInterval(() => {
      void loadRandomQuote();
    }, QUOTE_ROTATE_MS);
    return () => clearInterval(timer);
  }, [loadRandomQuote]);

  const renderLoginButtonContent = (label) =>
    loginPending ? <ActivityIndicator color="#000000" /> : <Text style={styles.loginButtonText}>{label}</Text>;

  const animateButtonPress = () => {
    Animated.sequence([
      Animated.timing(buttonScale, {
        toValue: 0.95,
        duration: 100,
        useNativeDriver: Platform.OS !== 'web',
      }),
      Animated.timing(buttonScale, {
        toValue: 1,
        duration: 100,
        useNativeDriver: Platform.OS !== 'web',
      }),
    ]).start();
  };

  const finishLogin = async (data) => {
    await setLoggedInStudentId(data.user?.id);
    invalidateStudentProfilePictureCache();
    const normalized = normalizeStudentUserRecord(data.user);
    await setUserSnapshot(normalized);
    setIsUSUserFromUser(normalized);
    navigation.replace('MainPage', { user: normalized });
  };

  const resetToCredentials = () => {
    setLoginStep('credentials');
    setLoginPendingToken(null);
    setPendingUser(null);
    setQrCodeDataUrl(null);
    setOtpCode('');
    setBackupCode('');
    setUseBackupCode(false);
    setStatusMessage('');
    setShowBackupCodesModal(false);
    setSetupBackupCodes([]);
    setPendingLoginAfterSetup(null);
    setShowReset2faModal(false);
    setReset2faPassword('');
  };

  const dismissBackupCodesModalAndContinue = async () => {
    const data = pendingLoginAfterSetup;
    setShowBackupCodesModal(false);
    setSetupBackupCodes([]);
    setPendingLoginAfterSetup(null);
    if (data) {
      await finishLogin(data);
    }
  };

  const handleLogin = async () => {
    if (!email || !password) {
      alert('Please enter both email and password');
      return;
    }

    animateButtonPress();
    setLoginPending(true);
    setStatusMessage('');

    try {
      const API_URL = getApiUrl();
      const response = await fetch(`${API_URL}/api/users/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await parseJsonResponse(response);

      if (!response.ok) {
        alert(data.error || `Login failed: ${response.status}`);
        return;
      }

      if (data.requires_2fa_setup) {
        setLoginPendingToken(data.login_pending_token);
        setPendingUser(data.user);
        setLoginStep('2fa_setup');
        setStatusMessage('Set up two-factor authentication to continue.');
        return;
      }

      if (data.requires_2fa_otp) {
        setLoginPendingToken(data.login_pending_token);
        setPendingUser(data.user);
        setLoginStep('2fa_otp');
        setStatusMessage('Enter the 6-digit code from your authenticator app.');
        return;
      }

      await finishLogin(data);
    } catch (error) {
      console.error('Login error:', error);
      alert(formatApiFetchError(error, getApiUrl()));
    } finally {
      setLoginPending(false);
    }
  };

  const handleSetupTwoFactor = async () => {
    if (!loginPendingToken) return;
    setLoginPending(true);
    setStatusMessage('');
    try {
      const API_URL = getApiUrl();
      const response = await fetch(`${API_URL}/api/auth/2fa/enable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ login_pending_token: loginPendingToken }),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        alert(data.error || 'Could not start two-factor setup');
        return;
      }
      setQrCodeDataUrl(data.qr_code_data_url);
      setLoginStep('2fa_setup_verify');
      setStatusMessage(data.message || 'Scan the QR code, then enter your first code below.');
    } catch (e) {
      alert(formatApiFetchError(e, getApiUrl()));
    } finally {
      setLoginPending(false);
    }
  };

  const handleVerifySetup = async () => {
    if (!loginPendingToken || !otpCode.trim()) {
      alert('Enter the 6-digit code from your authenticator app');
      return;
    }
    setLoginPending(true);
    try {
      const API_URL = getApiUrl();
      const response = await fetch(`${API_URL}/api/auth/2fa/verify-setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          login_pending_token: loginPendingToken,
          token: otpCode.trim(),
        }),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        alert(data.error || 'Verification failed');
        return;
      }
      const codes = Array.isArray(data.backup_codes) ? data.backup_codes : [];
      if (codes.length > 0) {
        setSetupBackupCodes(codes);
        setPendingLoginAfterSetup(data);
        setShowBackupCodesModal(true);
      } else {
        await finishLogin(data);
      }
    } catch (e) {
      alert(formatApiFetchError(e, getApiUrl()));
    } finally {
      setLoginPending(false);
    }
  };

  const handleResetAuthenticator = async (passwordFromModal) => {
    if (!loginPendingToken) return;
    const pw = passwordFromModal ?? reset2faPassword;
    if (!pw) {
      alert('Enter your password to reset authenticator setup');
      return;
    }
    setLoginPending(true);
    try {
      const API_URL = getApiUrl();
      const response = await fetch(`${API_URL}/api/auth/2fa/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          login_pending_token: loginPendingToken,
          password: pw,
        }),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        alert(data.error || 'Could not reset authenticator');
        return;
      }
      setShowReset2faModal(false);
      setReset2faPassword('');
      setLoginPendingToken(data.login_pending_token || loginPendingToken);
      setPendingUser(data.user || pendingUser);
      setQrCodeDataUrl(null);
      setOtpCode('');
      setLoginStep('2fa_setup');
      setStatusMessage(
        data.message ||
          'Authenticator reset. Tap Set up 2 Factor to scan a new QR code on your phone.'
      );
    } catch (e) {
      alert(formatApiFetchError(e, getApiUrl()));
    } finally {
      setLoginPending(false);
    }
  };

  const handleVerifyLoginOtp = async () => {
    if (!loginPendingToken) return;
    if (!useBackupCode && !otpCode.trim()) {
      alert('Enter your 6-digit authenticator code');
      return;
    }
    if (useBackupCode && !backupCode.trim()) {
      alert('Enter a backup recovery code');
      return;
    }
    setLoginPending(true);
    try {
      const API_URL = getApiUrl();
      const body = { login_pending_token: loginPendingToken };
      if (useBackupCode) {
        body.backup_code = backupCode.trim();
      } else {
        body.token = otpCode.trim();
      }
      const response = await fetch(`${API_URL}/api/auth/2fa/verify-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        alert(data.error || 'Verification failed');
        return;
      }
      await finishLogin(data);
    } catch (e) {
      alert(formatApiFetchError(e, getApiUrl()));
    } finally {
      setLoginPending(false);
    }
  };

  const styles = getStyles(theme, { isMobileWeb, isMfaStep: loginStep !== 'credentials', windowHeight });
  const credentialsLocked = loginStep !== 'credentials';
  const isCompactLayout = isMobileWeb && (loginStep !== 'credentials' || windowHeight < 740);
  const quoteType = quoteTypography(quote?.quote, isCompactLayout);
  const authorType = quoteTypography(quote?.who || '', isCompactLayout);

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      data-screen="login"
    >
      <StatusBar style="light" />

      <ScrollView
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        scrollEnabled={!isCompactLayout || loginStep === 'credentials'}
      >
        <View style={styles.logoContainer}>
          <ExpoImage
            source={{ uri: LOGO_URL }}
            style={styles.logo}
            contentFit="contain"
          />
        </View>

        <View style={styles.content}>
          {quote?.quote ? (
            <View
              style={styles.quoteContainer}
              {...(Platform.OS === 'web' ? { dataSet: { loginQuoteBlock: 'true' } } : {})}
            >
              <Text style={[styles.quoteText, quoteType]} numberOfLines={isCompactLayout ? 4 : 6}>
                &ldquo;{quote.quote}&rdquo;
              </Text>
              {quote.who ? (
                <Text
                  style={[
                    styles.quoteAuthor,
                    {
                      fontSize: Math.max(9, (authorType.fontSize || 12) - 2),
                      lineHeight: Math.max(12, (authorType.lineHeight || 16) - 2),
                    },
                  ]}
                  numberOfLines={2}
                >
                  — {quote.who}
                </Text>
              ) : null}
            </View>
          ) : null}

          <Text style={styles.title}>Ming Ch'uan</Text>
          <Text style={styles.subtitle}>Kung Fu</Text>

          <View style={styles.form}>
            {loginStep === 'credentials' ? (
              <>
                <View style={styles.inputContainer}>
                  <Text style={styles.label}>Email</Text>
                  <TextInput
                    style={styles.input}
                    placeholder="Enter your email"
                    placeholderTextColor={theme.isDarkMode ? '#999' : '#666'}
                    value={email}
                    onChangeText={setEmail}
                    keyboardType="email-address"
                    autoCapitalize="none"
                    autoCorrect={false}
                    editable={!loginPending}
                  />
                </View>

                <View style={styles.inputContainer}>
                  <Text style={styles.label}>Password</Text>
                  <View
                    style={styles.passwordFieldOuter}
                    {...(Platform.OS === 'web' ? { dataSet: { loginPasswordField: 'true' } } : {})}
                  >
                    <TextInput
                      style={styles.passwordInputInner}
                      placeholder="Enter your password"
                      placeholderTextColor={theme.isDarkMode ? '#999' : '#666'}
                      value={password}
                      onChangeText={setPassword}
                      secureTextEntry={!passwordVisible}
                      autoCapitalize="none"
                      autoCorrect={false}
                      textContentType="password"
                      editable={!loginPending}
                    />
                    <TouchableOpacity
                      onPress={() => setPasswordVisible((v) => !v)}
                      style={styles.passwordToggle}
                      activeOpacity={0.7}
                      accessibilityRole="button"
                      accessibilityLabel={passwordVisible ? 'Hide password' : 'Show password'}
                      hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
                      {...(Platform.OS === 'web' ? { dataSet: { loginPasswordToggle: 'true' } } : {})}
                    >
                      <MaterialIcons
                        name={passwordVisible ? 'visibility-off' : 'visibility'}
                        size={22}
                        color={LOGIN_GOLD}
                      />
                    </TouchableOpacity>
                  </View>
                </View>

                <Animated.View style={{ transform: [{ scale: buttonScale }] }}>
                  <TouchableOpacity
                    style={[styles.loginButton, loginPending && styles.loginButtonDisabled]}
                    onPress={handleLogin}
                    activeOpacity={0.8}
                    disabled={loginPending}
                  >
                    {renderLoginButtonContent('Login')}
                  </TouchableOpacity>
                </Animated.View>
              </>
            ) : null}

            {loginStep === '2fa_setup' ? (
              <>
                {pendingUser?.student_name || pendingUser?.username ? (
                  <Text style={styles.stepHint}>
                    Signed in as {pendingUser.student_name || pendingUser.username}
                  </Text>
                ) : null}
                <Text style={styles.stepHint}>
                  {statusMessage || 'Two-factor authentication is required for your account.'}
                </Text>
                <TouchableOpacity
                  style={[styles.twoFactorSetupButton, loginPending && styles.loginButtonDisabled]}
                  onPress={handleSetupTwoFactor}
                  disabled={loginPending}
                  activeOpacity={0.85}
                >
                  {loginPending ? (
                    <ActivityIndicator color="#FFFFFF" />
                  ) : (
                    <Text style={styles.twoFactorSetupButtonText}>Set up 2 Factor</Text>
                  )}
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.backLink}
                  onPress={() => setShowReset2faModal(true)}
                >
                  <Text style={styles.backLinkText}>New phone? Reset authenticator</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.backLink} onPress={resetToCredentials}>
                  <Text style={styles.backLinkText}>← Back to login</Text>
                </TouchableOpacity>
              </>
            ) : null}

            {loginStep === '2fa_setup_verify' ? (
              <>
                {qrCodeDataUrl ? (
                  <View style={styles.qrWrap}>
                    <ExpoImage
                      source={{ uri: qrCodeDataUrl }}
                      style={styles.qrImage}
                      contentFit="contain"
                      accessibilityLabel="Authenticator QR code"
                    />
                  </View>
                ) : null}
                <Text style={styles.stepHint}>
                  {statusMessage || 'Scan the QR code, then enter the 6-digit code.'}
                </Text>
                <View style={styles.inputContainer}>
                  <Text style={styles.label}>Authenticator code</Text>
                  <TextInput
                    style={styles.input}
                    placeholder="000000"
                    placeholderTextColor={theme.isDarkMode ? '#999' : '#666'}
                    value={otpCode}
                    onChangeText={setOtpCode}
                    keyboardType="number-pad"
                    maxLength={8}
                    autoCapitalize="none"
                    editable={!loginPending}
                  />
                </View>
                <TouchableOpacity
                  style={[styles.loginButton, loginPending && styles.loginButtonDisabled]}
                  onPress={handleVerifySetup}
                  disabled={loginPending}
                >
                  {renderLoginButtonContent('Verify & finish')}
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.backLink}
                  onPress={() => setShowReset2faModal(true)}
                >
                  <Text style={styles.backLinkText}>New phone? Reset authenticator</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.backLink} onPress={() => setLoginStep('2fa_setup')}>
                  <Text style={styles.backLinkText}>← Back</Text>
                </TouchableOpacity>
              </>
            ) : null}

            {loginStep === '2fa_otp' ? (
              <>
                {pendingUser?.student_name || pendingUser?.username ? (
                  <Text style={styles.stepHint}>
                    Welcome back, {pendingUser.student_name || pendingUser.username}
                  </Text>
                ) : null}
                <Text style={styles.stepHint}>{statusMessage}</Text>
                {!useBackupCode ? (
                  <View style={styles.inputContainer}>
                    <Text style={styles.label}>Authenticator code</Text>
                    <TextInput
                      style={styles.input}
                      placeholder="000000"
                      placeholderTextColor={theme.isDarkMode ? '#999' : '#666'}
                      value={otpCode}
                      onChangeText={setOtpCode}
                      keyboardType="number-pad"
                      maxLength={8}
                      editable={!loginPending}
                    />
                  </View>
                ) : (
                  <View style={styles.inputContainer}>
                    <Text style={styles.label}>Backup code</Text>
                    <TextInput
                      style={styles.input}
                      placeholder="XXXX-XXXX"
                      placeholderTextColor={theme.isDarkMode ? '#999' : '#666'}
                      value={backupCode}
                      onChangeText={setBackupCode}
                      autoCapitalize="characters"
                      editable={!loginPending}
                    />
                  </View>
                )}
                <TouchableOpacity
                  style={[styles.loginButton, loginPending && styles.loginButtonDisabled]}
                  onPress={handleVerifyLoginOtp}
                  disabled={loginPending}
                >
                  {renderLoginButtonContent('Verify & login')}
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.backLink}
                  onPress={() => {
                    setUseBackupCode((v) => !v);
                    setOtpCode('');
                    setBackupCode('');
                  }}
                >
                  <Text style={styles.backLinkText}>
                    {useBackupCode ? 'Use authenticator app instead' : 'Use a backup code instead'}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.backLink}
                  onPress={() => setShowReset2faModal(true)}
                >
                  <Text style={styles.backLinkText}>New phone? Reset authenticator</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.backLink} onPress={resetToCredentials}>
                  <Text style={styles.backLinkText}>← Back to login</Text>
                </TouchableOpacity>
              </>
            ) : null}

            {!credentialsLocked ? (
              <TouchableOpacity
                style={styles.forgotPassword}
                activeOpacity={0.7}
                onPress={() => navigation.navigate('ForgotPassword')}
              >
                <Text style={styles.forgotPasswordText}>Forgot Password?</Text>
              </TouchableOpacity>
            ) : null}
          </View>
        </View>
      </ScrollView>

      <Modal
        visible={showBackupCodesModal}
        transparent
        animationType="fade"
        onRequestClose={() => {}}
      >
        <View style={styles.backupModalOverlay}>
          <Pressable style={styles.backupModalBackdrop} onPress={() => {}} />
          <View style={styles.backupModalCard}>
            <Text style={styles.backupModalTitle}>Two-factor is now enabled</Text>
            <Text style={styles.backupModalBody}>
              This links your phone to the app.
            </Text>
            <Text style={styles.backupModalBody}>
              Screenshot these one-time use codes. This will help you log into the app if you do
              not have access to your phone or Authenticator app. Two Factor Authentication can
              also be reset.
            </Text>
            <View style={styles.backupCodesGrid}>
              {setupBackupCodes.map((code) => (
                <View key={code} style={styles.backupCodeCell}>
                  <Text style={styles.backupCodeText} selectable>
                    {code}
                  </Text>
                </View>
              ))}
            </View>
            <TouchableOpacity
              style={styles.backupModalButton}
              onPress={() => void dismissBackupCodesModalAndContinue()}
              activeOpacity={0.85}
            >
              <Text style={styles.backupModalButtonText}>Continue to app</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <TwoFactorResetModal
        visible={showReset2faModal}
        onClose={() => {
          if (!loginPending) {
            setShowReset2faModal(false);
            setReset2faPassword('');
          }
        }}
        title="Reset authenticator"
        description="Enter your password to clear this device’s authenticator setup. Backup codes and enrollment timestamps are removed. You can set up 2FA again on a new phone."
        onSubmit={async ({ password }) => {
          await handleResetAuthenticator(password);
        }}
        busy={loginPending}
      />
    </KeyboardAvoidingView>
  );
}

const getStyles = (theme, layout = {}) => {
  const { isMobileWeb = false, isMfaStep = false, windowHeight = 800 } = layout;
  const compactMfa = isMobileWeb && isMfaStep;

  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: '#000000',
    },
    scrollContent: {
      flexGrow: 1,
      backgroundColor: '#000000',
      ...(compactMfa && Platform.OS === 'web'
        ? { minHeight: windowHeight, justifyContent: 'flex-start' }
        : {}),
    },
    content: {
      flex: 1,
      width: '100%',
      paddingHorizontal: isMobileWeb ? 20 : 30,
      paddingTop: isMobileWeb ? 12 : 16,
      paddingBottom: compactMfa ? 16 : 40,
      justifyContent: 'flex-start',
      backgroundColor: '#000000',
    },
    logoContainer: {
      width: '100%',
      alignSelf: 'stretch',
      backgroundColor: '#000000',
    },
    logo: {
      width: '100%',
      aspectRatio: 1,
      borderWidth: 3,
      borderColor: LOGIN_GOLD,
      backgroundColor: '#000000',
    },
    quoteContainer: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: isMobileWeb ? 8 : 16,
      marginTop: -70,
      marginBottom: compactMfa ? 4 : 8,
      minHeight: compactMfa ? 40 : 52,
      backgroundColor: 'transparent',
    },
    quoteText: {
      color: 'rgba(255, 255, 255, 0.72)',
      fontStyle: 'italic',
      fontWeight: '400',
      textAlign: 'center',
      marginBottom: 4,
      backgroundColor: 'transparent',
      ...(Platform.OS === 'web' ? { fontFamily: 'serif' } : {}),
    },
    quoteAuthor: {
      color: 'rgba(255, 255, 255, 0.55)',
      fontWeight: '400',
      textAlign: 'right',
      width: '100%',
      paddingRight: isMobileWeb ? 8 : 24,
      fontStyle: 'normal',
      backgroundColor: 'transparent',
    },
    title: {
      fontSize: compactMfa ? 26 : isMobileWeb ? 28 : 32,
      fontWeight: 'bold',
      color: LOGIN_GOLD,
      textAlign: 'center',
      marginBottom: compactMfa ? 2 : 5,
      letterSpacing: 2,
      backgroundColor: 'transparent',
    },
    subtitle: {
      fontSize: compactMfa ? 16 : isMobileWeb ? 18 : 20,
      color: '#FFFFFF',
      textAlign: 'center',
      marginBottom: compactMfa ? 12 : isMobileWeb ? 18 : 30,
      letterSpacing: 1,
      backgroundColor: 'transparent',
    },
    form: {
      width: isMobileWeb ? '100%' : '80%',
      maxWidth: 420,
      alignSelf: 'center',
    },
    inputContainer: {
      marginBottom: compactMfa ? 12 : 20,
    },
    label: {
      fontSize: 14,
      color: LOGIN_GOLD,
      marginBottom: 8,
      fontWeight: '600',
    },
    input: {
      backgroundColor: '#2a2a2a',
      borderRadius: 8,
      paddingHorizontal: 16,
      paddingVertical: 14,
      fontSize: 16,
      color: '#FFFFFF',
      borderWidth: 1,
      borderColor: '#3a3a3a',
    },
    passwordFieldOuter: {
      position: 'relative',
      backgroundColor: '#2a2a2a',
      borderRadius: 8,
      borderWidth: 1,
      borderColor: '#3a3a3a',
      minHeight: 50,
      justifyContent: 'center',
    },
    passwordInputInner: {
      width: '100%',
      paddingLeft: 16,
      paddingRight: 52,
      paddingVertical: 14,
      fontSize: 16,
      color: '#FFFFFF',
      backgroundColor: '#2a2a2a',
      borderWidth: 0,
      ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {}),
    },
    passwordToggle: {
      position: 'absolute',
      right: 4,
      top: 0,
      bottom: 0,
      width: 44,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: 'transparent',
      zIndex: 2,
      ...(Platform.OS === 'web' ? { cursor: 'pointer', outlineStyle: 'none' } : {}),
    },
    loginButton: {
      backgroundColor: LOGIN_GOLD,
      borderRadius: 8,
      paddingVertical: 16,
      alignItems: 'center',
      marginTop: 12,
      width: '100%',
    },
    loginButtonDisabled: {
      opacity: 0.85,
    },
    loginButtonText: {
      color: '#000000',
      fontSize: 18,
      fontWeight: 'bold',
      letterSpacing: 1,
    },
    twoFactorSetupButton: {
      backgroundColor: LOGIN_GOLD,
      borderRadius: 8,
      paddingVertical: 16,
      alignItems: 'center',
      marginTop: 16,
      width: '100%',
    },
    twoFactorSetupButtonText: {
      color: '#FFFFFF',
      fontSize: 18,
      fontWeight: '700',
      letterSpacing: 0.5,
    },
    stepHint: {
      color: '#CCCCCC',
      fontSize: compactMfa ? 13 : 14,
      lineHeight: compactMfa ? 18 : 20,
      marginBottom: compactMfa ? 8 : 12,
      textAlign: 'center',
      backgroundColor: 'transparent',
    },
    qrWrap: {
      alignItems: 'center',
      marginBottom: compactMfa ? 10 : 16,
      padding: compactMfa ? 8 : 12,
      backgroundColor: '#FFFFFF',
      borderRadius: 10,
      alignSelf: 'center',
    },
    qrImage: {
      width: compactMfa ? 160 : 220,
      height: compactMfa ? 160 : 220,
    },
    backLink: {
      marginTop: 16,
      alignItems: 'center',
    },
    backLinkText: {
      color: LOGIN_GOLD,
      fontSize: 14,
    },
    forgotPassword: {
      marginTop: 20,
      alignItems: 'center',
    },
    forgotPasswordText: {
      color: '#999',
      fontSize: 14,
    },
    backupModalOverlay: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 16,
    },
    backupModalBackdrop: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: 'rgba(0, 0, 0, 0.85)',
    },
    backupModalCard: {
      width: '100%',
      maxWidth: 420,
      backgroundColor: '#1a1a1a',
      borderRadius: 12,
      borderWidth: 2,
      borderColor: LOGIN_GOLD,
      paddingVertical: 18,
      paddingHorizontal: 16,
      zIndex: 1,
      ...(Platform.OS === 'web'
        ? { maxHeight: '92vh', boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }
        : { maxHeight: '90%' }),
    },
    backupModalTitle: {
      fontSize: 18,
      fontWeight: '700',
      color: LOGIN_GOLD,
      textAlign: 'center',
      marginBottom: 10,
    },
    backupModalBody: {
      fontSize: 13,
      lineHeight: 19,
      color: '#E8E8E8',
      marginBottom: 10,
      textAlign: 'left',
    },
    backupCodesGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      marginTop: 4,
      marginBottom: 14,
      backgroundColor: '#0d0d0d',
      borderRadius: 8,
      borderWidth: 1,
      borderColor: '#3a3a3a',
      paddingVertical: 10,
      paddingHorizontal: 6,
    },
    backupCodeCell: {
      width: '50%',
      paddingVertical: 5,
      paddingHorizontal: 8,
      alignItems: 'center',
    },
    backupCodeText: {
      fontSize: 13,
      fontWeight: '600',
      color: '#FFFFFF',
      letterSpacing: 1,
      fontFamily: Platform.OS === 'ios' ? 'Menlo' : Platform.OS === 'android' ? 'monospace' : 'monospace',
    },
    backupModalButton: {
      backgroundColor: LOGIN_GOLD,
      borderRadius: 8,
      paddingVertical: 14,
      alignItems: 'center',
    },
    backupModalButtonText: {
      color: '#000000',
      fontSize: 16,
      fontWeight: '700',
    },
  });
};
